// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { CustomEvent } from '@shared/utils/CustomEvents';
import { Rectangle } from '@shared/commons/Geometry';
import { ThemeManager } from '../misc/ThemeManager';
import { SolidBrush } from '@shared/commons/Graphics';
export class Scroll {
    get Visible() {
        return this._visible;
    }
    set Visible(value) {
        this._visible = value;
        if (this._visible) {
            this.showScrollbar();
        }
        else if (this.autoHideEnabled) {
            this.hideScrollbar();
        }
    }
    constructor(x, y, width, height, scrollStartIndex, scrollElementsCount, ctx, elementHeight, isAttached, autoHideEnabled = true) {
        this.isSliderMove = false;
        this.OnValueChange = new CustomEvent();
        this.maxElementsOnScreen = 0;
        this.Hidden = false;
        this.HiddenByScrollAutohide = false;
        this._visible = false;
        this.autoHideTimeout = 2000; // Time in milliseconds to hide scrollbar after inactivity
        this.hideScrollbarTimeoutId = null;
        this.autoHideEnabled = true; // Enable/disable auto-hide functionality
        this.fadeOpacity = 1.0;
        this.updateScrollControlsData = this.updateScrollControlsData.bind(this);
        this.scrollIndex = scrollStartIndex;
        this.scrollElementsCount = scrollElementsCount;
        this.ctx = ctx;
        this.elementHeight = elementHeight;
        this.isAttached = isAttached;
        this.autoHideEnabled = autoHideEnabled;
        this.initScrollControls();
        this.setBounds(new Rectangle(x, y, width, height));
    }
    isActive() {
        return this.Visible && (!this.autoHideEnabled || (this.autoHideEnabled && !this.HiddenByScrollAutohide));
    }
    initScrollControls() {
        this.btnUp = new ScrollElement(new Rectangle(0, 0, Scroll.SCROLL_BTN_WIDTH, Scroll.SCROLL_BTN_HEIGHT), VisualControlState.None);
        this.btnDown = new ScrollElement(new Rectangle(0, 0, Scroll.SCROLL_BTN_WIDTH, Scroll.SCROLL_BTN_HEIGHT), VisualControlState.None);
        this.slider = new ScrollElement(new Rectangle(0, 0, Scroll.SCROLL_SLIDER_WIDTH, 0), VisualControlState.None);
        this.controls = [this.btnUp, this.slider, this.btnDown];
    }
    // #region Draw methods
    Draw() {
        if (!this.Visible || this.Hidden) {
            return;
        }
        const ctx = this.ctx;
        if (isNullOrUndefined(this.ctx)) {
            return;
        }
        ctx.save();
        // Set the globalAlpha only for scrollbar drawing
        ctx.globalAlpha = this.fadeOpacity;
        ctx.imageSmoothingEnabled = false;
        const myRect = this.containerRectangle;
        // clear Place
        ctx.fillStyle = this.isAttached
            ? ThemeManager.colors.attachedScrollBackgroundColor
            : ThemeManager.colors.detachedScrollBackgroundColor;
        ctx.fillRect(myRect.X, myRect.Y, myRect.Width, myRect.Height);
        Scroll.drawSlider(ctx, this.slider.rectangle, this.slider.visualState);
        ctx.globalAlpha = 1;
        ctx.restore();
    }
    static drawSlider(ctx, rect, visualState) {
        const color = visualState === VisualControlState.None
            ? ThemeManager.colors.scrollDraggerBackground
            : ThemeManager.colors.scrollDraggerBackgroundHovered;
        const brush = new SolidBrush(color);
        ctx.RoundRectangle(rect, 4, brush);
    }
    // #endregion
    setScrollElementsCount(newCount, forceUpdate = false) {
        if (this.scrollElementsCount === newCount && !forceUpdate) {
            return;
        }
        this.scrollElementsCount = newCount;
        this.updateScrollControlsData();
    }
    setBounds(rect) {
        if (isNullOrUndefined(rect))
            return;
        const cRect = this.containerRectangle;
        if (cRect === null || cRect === void 0 ? void 0 : cRect.Equals(rect))
            return;
        this.containerRectangle = rect;
        this.updateScrollControlsData();
        this.scrollIndex = this.correctScrollIndex(this.scrollIndex);
    }
    updateScrollControlsData() {
        const cRect = this.containerRectangle;
        const elementsTotal = this.scrollElementsCount;
        // Count only entirely visible elements.
        const maxElementsOnScreen = Math.floor(this.containerRectangle.Height / this.elementHeight);
        const allElementsFitScreen = elementsTotal <= maxElementsOnScreen;
        const availableH = cRect.Height - 2 * Scroll.SCROLL_BTN_HEIGHT;
        let scaledElementH = elementsTotal ? availableH / elementsTotal : 1;
        let sliderH = allElementsFitScreen ? availableH : maxElementsOnScreen * scaledElementH;
        if (sliderH < Scroll.SCROLL_SLIDER_MIN_HEIGHT) {
            sliderH = Scroll.SCROLL_SLIDER_MIN_HEIGHT;
            scaledElementH = (availableH - sliderH) / (elementsTotal - maxElementsOnScreen);
        }
        const scrollControlLeft = cRect.X + 1;
        const slider = this.slider;
        slider.rectangle.X = scrollControlLeft;
        slider.rectangle.Height = sliderH;
        const btnUpRect = this.btnUp.rectangle;
        btnUpRect.X = scrollControlLeft;
        btnUpRect.Y = cRect.Y;
        const btnDownRect = this.btnDown.rectangle;
        btnDownRect.X = scrollControlLeft;
        btnDownRect.Y = cRect.Height + cRect.Y - Scroll.SCROLL_BTN_HEIGHT;
        this.minY = cRect.Y + Scroll.SCROLL_BTN_HEIGHT;
        this.maxY = cRect.Y + cRect.Height - Scroll.SCROLL_BTN_HEIGHT - sliderH;
        this.scaledElementH = scaledElementH;
        this.maxElementsOnScreen = maxElementsOnScreen;
        this.Visible = !allElementsFitScreen;
        if (!this.Visible && this.scrollIndex !== 0) {
            this.moveScrollToElement(0);
        }
        else {
            this.moveScrollToElement(Math.min(this.scrollIndex, this.scrollElementsCount - this.maxElementsOnScreen));
        }
    }
    trailingScroll(leadingIdx) {
        if (leadingIdx <= this.scrollIndex) {
            this.moveScrollToElement(leadingIdx);
            return;
        }
        const bottomScrollIndex = this.scrollIndex + this.maxElementsOnScreen - 1;
        if (this.scrollIndex <= leadingIdx && leadingIdx <= bottomScrollIndex) {
            return;
        }
        this.moveScrollToElement(this.scrollIndex + leadingIdx - bottomScrollIndex);
    }
    correctScrollIndex(preferredIndex) {
        const maxElementsOnScreen = this.maxElementsOnScreen;
        if (maxElementsOnScreen >= this.scrollElementsCount) {
            return 0;
        }
        if (preferredIndex < 0) {
            return 0;
        }
        if (preferredIndex >= this.scrollElementsCount) {
            return this.scrollElementsCount - maxElementsOnScreen - 1;
        }
        const elementsBelowScreen = this.scrollElementsCount - preferredIndex - maxElementsOnScreen;
        if (elementsBelowScreen >= 0) {
            return preferredIndex;
        }
        // Shifting scroll index back.
        preferredIndex += elementsBelowScreen;
        if (preferredIndex < 0) {
            preferredIndex = 0;
        }
        return preferredIndex;
    }
    moveScrollToElement(index) {
        if (index !== this.scrollIndex) {
            const oldScrollIndex = this.scrollIndex;
            this.scrollIndex = this.correctScrollIndex(index);
            if (oldScrollIndex !== this.scrollIndex) {
                this.OnValueChange.Raise(this.scrollIndex);
            }
        }
        let newY = this.minY + this.scrollIndex * this.scaledElementH;
        if (newY > this.maxY) {
            newY = this.maxY;
        }
        this.slider.rectangle.Y = newY;
        if (this.isActive()) {
            this.Draw();
        }
    }
    moveSliderToY(newY) {
        if (newY < this.minY) {
            newY = this.minY;
        }
        else if (newY > this.maxY) {
            newY = this.maxY;
        }
        this.slider.rectangle.Y = newY;
        this.scrollIndex = Math.round((newY - this.minY) / this.scaledElementH);
        this.OnValueChange.Raise(this.scrollIndex);
        if (this.isActive()) {
            this.Draw();
        }
    }
    mouseWheel(isUp) {
        this.moveScrollToElement(this.scrollIndex + (isUp ? -1 : 1));
    }
    mouseDown(event) {
        this.showScrollbar();
        if (!this.isActive()) {
            return;
        }
        // find part of scroll
        if (this.slider.rectangle.Contains(event.offsetX, event.offsetY)) {
            this.slider.visualState = VisualControlState.Clicked;
            this.isSliderMove = true;
            this.moveDeltaY = event.offsetY - this.slider.rectangle.Y;
            this.Draw();
        }
        else if (this.btnUp.rectangle.Contains(event.offsetX, event.offsetY)) {
            this.btnUp.visualState = VisualControlState.Clicked;
            this.mouseWheel(true);
        }
        else if (this.btnDown.rectangle.Contains(event.offsetX, event.offsetY)) {
            this.btnDown.visualState = VisualControlState.Clicked;
            this.mouseWheel(false);
        }
        else {
            this.moveSliderToY(event.offsetY);
        }
    }
    mouseMove(event) {
        this.showScrollbar();
        if (!this.Visible || !this.isSliderMove) {
            return;
        }
        this.slider.visualState = VisualControlState.Clicked;
        this.moveSliderToY(event.offsetY - this.moveDeltaY);
    }
    // TODO. Qtable, qtree call updateControlHoverVisualState.
    // For updating hover/default state only.
    onMouseMove(event) {
        this.showScrollbar();
        this.updateControlHoverVisualState(event);
    }
    mouseUp(event) {
        this.isSliderMove = false;
        // TODO.
        // Corrects slider y position.
        this.moveScrollToElement(this.scrollIndex);
    }
    showScrollbar() {
        if (this.autoHideEnabled) {
            this.HiddenByScrollAutohide = false;
            this.fadeOpacity = 1.0; // Reset opacity
            if (this.hideScrollbarTimeoutId != null) {
                clearTimeout(this.hideScrollbarTimeoutId);
            }
            this.hideScrollbarTimeoutId = setTimeout(() => {
                this.hideScrollbar();
            }, this.autoHideTimeout);
        }
        this.Draw();
    }
    hideScrollbar() {
        this.fadeOutScrollbar();
    }
    setAutoHideEnabled(enabled) {
        this.autoHideEnabled = enabled;
        if (enabled) {
            this.showScrollbar();
        }
        else {
            this.HiddenByScrollAutohide = false;
            this.Draw();
        }
    }
    updateControlHoverVisualState(event) {
        if (!this.isActive()) {
            return;
        }
        const controls = this.controls;
        const len = controls.length;
        for (let i = 0; i < len; i++) {
            const control = controls[i];
            control.visualState = control.rectangle.Contains(event.offsetX, event.offsetY)
                ? VisualControlState.Hovered
                : VisualControlState.None;
        }
        this.Draw();
    }
    getVisibleCount() {
        const rect = this.containerRectangle;
        if (isNullOrUndefined(rect))
            return this.maxElementsOnScreen;
        const full = this.maxElementsOnScreen;
        const rest = rect.Height - full * this.elementHeight;
        return full + (rest > 0 ? 1 : 0);
    }
    themeChange() { }
    isNeedRedrawAllLayout() {
        return this.autoHideEnabled && this.fadeOpacity < 1 && !this.HiddenByScrollAutohide;
    }
    fadeOutScrollbar() {
        const step = 0.05;
        const interval = 50; // ms
        const fadeStep = () => {
            this.fadeOpacity -= step;
            if (this.fadeOpacity < 0) {
                // this.OnValueChange.Raise(this.scrollIndex);
                this.fadeOpacity = 0;
                this.HiddenByScrollAutohide = true;
            }
        };
        const fadeInterval = setInterval(() => {
            fadeStep();
            if (this.HiddenByScrollAutohide) {
                clearInterval(fadeInterval);
            }
        }, interval);
    }
}
Scroll.SCROLL_BTN_WIDTH = 0;
Scroll.SCROLL_BTN_HEIGHT = 1;
Scroll.SCROLL_WIDTH = 8;
Scroll.SCROLL_SLIDER_WIDTH = 4;
Scroll.SCROLL_SLIDER_LEFT = 2;
Scroll.SCROLL_SLIDER_MIN_HEIGHT = 17;
class ScrollElement {
    constructor(rectangle, visualState) {
        this.rectangle = rectangle;
        this.visualState = visualState;
    }
}
export var VisualControlState;
(function (VisualControlState) {
    VisualControlState[VisualControlState["None"] = 0] = "None";
    VisualControlState[VisualControlState["Clicked"] = 1] = "Clicked";
    VisualControlState[VisualControlState["Hovered"] = 2] = "Hovered";
})(VisualControlState || (VisualControlState = {}));
